import React from 'react';
import { Tab, Table, Button, Icon, Input, Segment, Menu, Label } from 'semantic-ui-react';
import { FieldArray, Field } from 'formik';
import { MaterialTextArea } from '../inputs';
import styled from 'styled-components/macro';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';

const DateRangeWrap = styled.div`
	display: flex;
	align-items: center;
	gap: 10px;
	margin-bottom: 10px;

	.date-input {
		display: flex;
		align-items: center;
		gap: 5px;
	}

	.date-separator {
		color: #888;
		font-weight: bold;
	}

	select {
		padding: 6px 10px;
		border: 1px solid #ddd;
		border-radius: 4px;
		background: white;
	}
`;

const DayItem = styled.div`
	background: ${(props) => (props.isActive ? '#e8f5e9' : '#f9f9f9')};
	border: 1px solid ${(props) => (props.isActive ? '#81c784' : '#e0e0e0')};
	border-radius: 8px;
	padding: 16px;
	margin-bottom: 12px;
	position: relative;

	.day-header {
		display: flex;
		justify-content: space-between;
		align-items: center;
		margin-bottom: 12px;
	}

	.day-title {
		font-weight: 600;
		color: #333;
	}

	.message-area {
		margin-top: 10px;
	}

	textarea {
		width: 100%;
		min-height: 60px;
	}

	.active-badge {
		position: absolute;
		top: 8px;
		right: 60px;
		background: #4caf50;
		color: white;
		padding: 2px 8px;
		border-radius: 4px;
		font-size: 11px;
	}
`;

const AddButton = styled(Button)`
	margin-top: 10px !important;
`;

const ProductTabWrap = styled.div`
	.add-product-btn {
		margin-left: 10px;
	}
`;

const EmptyState = styled.div`
	text-align: center;
	padding: 40px;
	color: #888;

	.icon {
		font-size: 48px !important;
		margin-bottom: 16px;
		color: #ccc;
	}
`;

// Bugünün tarihinin aralık içinde olup olmadığını kontrol et
function isDateInRange(startDate, endDate) {
	if (!startDate || !endDate) return false;

	const today = new Date();
	const currentMonth = today.getMonth() + 1;
	const currentDay = today.getDate();
	const todayStr = `${String(currentMonth).padStart(2, '0')}-${String(currentDay).padStart(2, '0')}`;

	// Basit string karşılaştırması (MM-DD formatı)
	if (startDate <= endDate) {
		// Normal aralık (örn: 01-01 ile 01-15)
		return todayStr >= startDate && todayStr <= endDate;
	} else {
		// Yıl geçişli aralık (örn: 12-25 ile 01-05)
		return todayStr >= startDate || todayStr <= endDate;
	}
}

// Ay isimleri - i18n key'leri
const MONTH_KEYS = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december'];

// Ay listesini i18n ile oluştur
function getMonths(t) {
	return MONTH_KEYS.map((key, index) => ({
		value: String(index + 1).padStart(2, '0'),
		text: t(key),
	}));
}

// Gün listesi (1-31)
const DAYS = Array.from({ length: 31 }, (_, i) => ({
	value: String(i + 1).padStart(2, '0'),
	text: String(i + 1),
}));

// MM-DD formatından ay ve gün ayır
function parseDate(mmdd) {
	if (!mmdd) return { month: '01', day: '01' };
	const [month, day] = mmdd.split('-');
	return { month: month || '01', day: day || '01' };
}

// Ay ve günü MM-DD formatına çevir
function formatDate(month, day) {
	return `${month}-${day}`;
}

// Tarih aralığı geçerli mi kontrol et
// Aralık ayından başlıyorsa Ocak-Mart'a kadar yıl geçişine izin ver
function isValidDateRange(startDate, endDate) {
	if (!startDate || !endDate) return true;

	const startMonth = startDate.split('-')[0];
	const endMonth = endDate.split('-')[0];

	// Aralık ayından başlıyorsa
	if (startMonth === '12') {
		// Ocak, Şubat, Mart'a yıl geçişi OK
		if (['01', '02', '03'].includes(endMonth)) {
			return true;
		}
		// Aralık içinde kalıyorsa normal kontrol
		if (endMonth === '12') {
			return endDate >= startDate;
		}
		// Diğer aylara geçiş yasak
		return false;
	}

	// Diğer aylar için bitiş >= başlangıç olmalı
	return endDate >= startDate;
}

// Başlangıç tarihine göre geçerli bitiş aylarını döndür
function getValidEndOptions(startDate) {
	const allMonths = MONTH_KEYS.map((_, i) => String(i + 1).padStart(2, '0'));
	if (!startDate) return { months: allMonths };

	const startMonth = startDate.split('-')[0];
	const startMonthIndex = parseInt(startMonth, 10);

	let validMonths = [];

	if (startMonth === '12') {
		// Aralık: Aralık + Ocak, Şubat, Mart
		validMonths = ['12', '01', '02', '03'];
	} else {
		// Diğer aylar: başlangıç ayından sonraki tüm aylar (Aralık dahil değil çünkü yıl geçişi sadece Aralık'tan başlarsa olur)
		for (let i = startMonthIndex; i <= 12; i++) {
			validMonths.push(String(i).padStart(2, '0'));
		}
	}

	return { months: validMonths };
}

// Varsayılan özel günler - i18n key'leri kullanıyor
const DEFAULT_SPECIAL_DAYS_KEYS = [
	{ startDate: '01-01', endDate: '01-01', messageKey: 'newYearMessage', nameKey: 'newYear' },
	{ startDate: '02-14', endDate: '02-14', messageKey: 'valentinesDayMessage', nameKey: 'valentinesDay' },
	{ startDate: '03-08', endDate: '03-08', messageKey: 'womensDayMessage', nameKey: 'womensDay' },
	{ startDate: '04-23', endDate: '04-23', messageKey: 'april23Message', nameKey: 'april23' },
	{ startDate: '05-10', endDate: '05-10', messageKey: 'mothersDayMessage', nameKey: 'mothersDay' },
	{ startDate: '05-19', endDate: '05-19', messageKey: 'may19Message', nameKey: 'may19' },
	{ startDate: '06-21', endDate: '06-21', messageKey: 'fathersDayMessage', nameKey: 'fathersDay' },
	{ startDate: '08-30', endDate: '08-30', messageKey: 'august30Message', nameKey: 'august30' },
	{ startDate: '10-29', endDate: '10-29', messageKey: 'republicDayMessage', nameKey: 'republicDay' },
	{ startDate: '11-24', endDate: '11-24', messageKey: 'teachersDayMessage', nameKey: 'teachersDay' },
];

// Varsayılan günleri i18n ile oluştur
function getDefaultSpecialDays(t) {
	return DEFAULT_SPECIAL_DAYS_KEYS.map((day) => ({
		startDate: day.startDate,
		endDate: day.endDate,
		message: t(day.messageKey),
		name: t(day.nameKey),
	}));
}

const DEFAULT_PRODUCTS = ['flo', 'instreet'];

function SpecialDayItem({ index, arrayHelpers, item }) {
	const { t } = useTranslation();
	const isActive = isDateInRange(item.startDate, item.endDate);
	const MONTHS = getMonths(t);

	return (
		<DayItem isActive={isActive}>
			{isActive && <span className="active-badge">📅 {t('todayActive')}</span>}
			<div className="day-header">
				<Field name={`${arrayHelpers.name}.${index}.name`}>
					{({ field, form }) => (
						<Input
							placeholder={t('dayName')}
							value={field.value || ''}
							onChange={(e, { value }) => form.setFieldValue(field.name, value)}
							style={{ width: '200px' }}
						/>
					)}
				</Field>
				<Button type="button" icon color="red" size="tiny" onClick={() => arrayHelpers.remove(index)}>
					<Icon name="trash" />
				</Button>
			</div>

			<DateRangeWrap>
				<Field name={`${arrayHelpers.name}.${index}.startDate`}>
					{({ field, form }) => {
						const { month, day } = parseDate(field.value);
						return (
							<div className="date-input">
								<select
									value={day}
									onChange={(e) => {
										const newStart = formatDate(month, e.target.value);
										form.setFieldValue(field.name, newStart);
										// Bitiş tarihini kontrol et
										const endDate = _.get(form.values, `${arrayHelpers.name}.${index}.endDate`);
										if (endDate && !isValidDateRange(newStart, endDate)) {
											form.setFieldValue(`${arrayHelpers.name}.${index}.endDate`, newStart);
										}
									}}
								>
									{DAYS.map((d) => (
										<option key={d.value} value={d.value}>
											{d.text}
										</option>
									))}
								</select>
								<select
									value={month}
									onChange={(e) => {
										const newStart = formatDate(e.target.value, day);
										form.setFieldValue(field.name, newStart);
										// Bitiş tarihini kontrol et
										const endDate = _.get(form.values, `${arrayHelpers.name}.${index}.endDate`);
										if (endDate && !isValidDateRange(newStart, endDate)) {
											form.setFieldValue(`${arrayHelpers.name}.${index}.endDate`, newStart);
										}
									}}
								>
									{MONTHS.map((m) => (
										<option key={m.value} value={m.value}>
											{m.text}
										</option>
									))}
								</select>
							</div>
						);
					}}
				</Field>
				<span className="date-separator">—</span>
				<Field name={`${arrayHelpers.name}.${index}.startDate`}>
					{({ field: startField }) => (
						<Field name={`${arrayHelpers.name}.${index}.endDate`}>
							{({ field, form }) => {
								const { month, day } = parseDate(field.value);
								const startDate = startField.value;
								const validEndOptions = getValidEndOptions(startDate);

								return (
									<div className="date-input">
										<select
											value={day}
											onChange={(e) => {
												const newEnd = formatDate(month, e.target.value);
												if (isValidDateRange(startDate, newEnd)) {
													form.setFieldValue(field.name, newEnd);
												}
											}}
										>
											{DAYS.map((d) => {
												const testDate = formatDate(month, d.value);
												const isDisabled = !isValidDateRange(startDate, testDate);
												return (
													<option key={d.value} value={d.value} disabled={isDisabled}>
														{d.text}
													</option>
												);
											})}
										</select>
										<select
											value={month}
											onChange={(e) => {
												const newEnd = formatDate(e.target.value, day);
												if (isValidDateRange(startDate, newEnd)) {
													form.setFieldValue(field.name, newEnd);
												} else {
													// Ay değişince gün geçersiz olabilir, başlangıç gününü kullan
													const startParsed = parseDate(startDate);
													const fallbackEnd = formatDate(e.target.value, startParsed.day);
													if (isValidDateRange(startDate, fallbackEnd)) {
														form.setFieldValue(field.name, fallbackEnd);
													}
												}
											}}
										>
											{MONTHS.map((m) => {
												const isDisabled = !validEndOptions.months.includes(m.value);
												return (
													<option key={m.value} value={m.value} disabled={isDisabled}>
														{m.text}
													</option>
												);
											})}
										</select>
									</div>
								);
							}}
						</Field>
					)}
				</Field>
			</DateRangeWrap>

			<div className="message-area">
				<Field name={`${arrayHelpers.name}.${index}.message`}>
					{({ field, form }) => (
						<textarea
							placeholder={t('message')}
							value={field.value || ''}
							onChange={(e) => form.setFieldValue(field.name, e.target.value)}
							className="ui input"
							style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ddd' }}
						/>
					)}
				</Field>
			</div>
		</DayItem>
	);
}

function ProductDaysList({ productKey, form }) {
	const { t } = useTranslation();
	const basePath = `special_days.${productKey}`;

	return (
		<FieldArray
			name={basePath}
			render={(arrayHelpers) => {
				const days = _.get(form.values, basePath) || [];

				return (
					<div>
						{days.length === 0 ? (
							<EmptyState>
								<Icon name="calendar outline" />
								<p>{t('noSpecialDays')}</p>
							</EmptyState>
						) : (
							days.map((item, index) => <SpecialDayItem key={index} index={index} arrayHelpers={arrayHelpers} item={item} />)
						)}

						<AddButton
							type="button"
							color="green"
							onClick={() => arrayHelpers.push({ startDate: '01-01', endDate: '01-01', message: '', name: '' })}
						>
							<Icon name="plus" />
							{t('addSpecialDay')}
						</AddButton>

						{days.length === 0 && (
							<Button
								type="button"
								color="blue"
								style={{ marginLeft: '10px', marginTop: '10px' }}
								onClick={() => {
									getDefaultSpecialDays(t).forEach((day) => arrayHelpers.push({ ...day }));
								}}
							>
								<Icon name="magic" />
								{t('addDefaultDays')}
							</Button>
						)}
					</div>
				);
			}}
		/>
	);
}

function AddProductModal({ onAdd, existingProducts }) {
	const [productName, setProductName] = React.useState('');
	const { t } = useTranslation();

	const handleAdd = () => {
		if (productName.trim() && !existingProducts.includes(productName.toLowerCase().trim())) {
			onAdd(productName.toLowerCase().trim());
			setProductName('');
		}
	};

	return (
		<div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
			<Input
				placeholder={t('productName')}
				value={productName}
				onChange={(e, { value }) => setProductName(value)}
				onKeyPress={(e) => e.key === 'Enter' && handleAdd()}
			/>
			<Button type="button" color="teal" onClick={handleAdd} disabled={!productName.trim()}>
				<Icon name="plus" />
				{t('addProduct')}
			</Button>
		</div>
	);
}

function SpecialDaysTab({ values, setFieldValue }) {
	const { t } = useTranslation();
	const [activeProduct, setActiveProduct] = React.useState(0);

	// special_days objesi yoksa oluştur
	const specialDays = values.special_days || {};
	const products = Object.keys(specialDays).length > 0 ? Object.keys(specialDays) : [];

	const handleAddProduct = (productName) => {
		const newSpecialDays = {
			...specialDays,
			[productName]: [],
		};
		setFieldValue('special_days', newSpecialDays);
	};

	const handleRemoveProduct = (productName) => {
		const newSpecialDays = { ...specialDays };
		delete newSpecialDays[productName];
		setFieldValue('special_days', newSpecialDays);
		setActiveProduct(0);
	};

	const productPanes = products.map((product, index) => ({
		menuItem: (
			<Menu.Item key={product} active={activeProduct === index} onClick={() => setActiveProduct(index)}>
				{product.toUpperCase()}
				<Icon
					name="close"
					style={{ marginLeft: '8px', opacity: 0.6 }}
					onClick={(e) => {
						e.stopPropagation();
						if (window.confirm(t('confirmDeleteProduct', { product }))) {
							handleRemoveProduct(product);
						}
					}}
				/>
			</Menu.Item>
		),
		render: () => (
			<Tab.Pane key={product}>
				<ProductDaysList productKey={product} form={{ values, setFieldValue }} />
			</Tab.Pane>
		),
	}));

	return (
		<Tab.Pane>
			<ProductTabWrap>
				<AddProductModal onAdd={handleAddProduct} existingProducts={products} />

				{products.length === 0 ? (
					<Segment placeholder>
						<div style={{ textAlign: 'center', padding: '40px' }}>
							<Icon name="box" size="huge" style={{ color: '#ccc', marginBottom: '16px' }} />
							<h3>{t('noProducts')}</h3>
							<p>{t('addProductHint')}</p>
						</div>
					</Segment>
				) : (
					<Tab
						menu={{ secondary: true, pointing: true }}
						activeIndex={activeProduct}
						panes={productPanes}
						onTabChange={(e, { activeIndex }) => setActiveProduct(activeIndex)}
					/>
				)}
			</ProductTabWrap>
		</Tab.Pane>
	);
}

export default SpecialDaysTab;
